/**
 * Argus — toast global pentru notificare USB plug/unplug real-time.
 *
 * Mount-uit în App.tsx lângă alte componente globale. Ascultă CustomEvent
 * `argus:device-event` (emit de wsClient când primește `device_event_push`)
 * și afișează MUI Snackbar top-right cu auto-dismiss 5s.
 *
 * Click toast → navigate la `/agents/:agentId/devices`.
 *
 * Bloc continuous-monitoring-usb 2026-05-06.
 */

import { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  DEVICE_EVENT_CUSTOM_EVENT,
  DEVICE_EVENT_LABEL,
  type DeviceEventPushPayload,
} from '../lib/deviceEvents';

interface ToastEntry {
  readonly id: number;
  readonly payload: DeviceEventPushPayload;
}

const SEVERITY_BY_EVENT = {
  usb_connected: 'success',
  usb_disconnected: 'info',
  drive_mounted: 'success',
  drive_unmounted: 'info',
} as const;

let nextToastId = 1;

function buildMessage(p: DeviceEventPushPayload): string {
  const label = DEVICE_EVENT_LABEL[p.event];
  const target = p.driveLetter
    ? p.label
      ? `${p.driveLetter} (${p.label})`
      : p.driveLetter
    : p.friendlyName ?? p.serial ?? '—';
  const host = p.hostname ?? p.agentId.slice(0, 8);
  return `${label}: ${target} pe ${host}`;
}

export function DeviceEventsToast(): JSX.Element {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const navigate = useNavigate();

  const handleEvent = useCallback((event: Event) => {
    const ce = event as CustomEvent<DeviceEventPushPayload>;
    if (!ce.detail) return;
    const id = nextToastId++;
    setEntries((prev) => [...prev, { id, payload: ce.detail }]);
  }, []);

  useEffect(() => {
    window.addEventListener(DEVICE_EVENT_CUSTOM_EVENT, handleEvent);
    return () => window.removeEventListener(DEVICE_EVENT_CUSTOM_EVENT, handleEvent);
  }, [handleEvent]);

  function handleClose(id: number): void {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }

  function handleClick(payload: DeviceEventPushPayload, id: number): void {
    navigate(`/agents/${payload.agentId}/devices`);
    handleClose(id);
  }

  return (
    <Stack
      sx={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 1500,
        gap: 1,
        maxWidth: 380,
      }}
    >
      {entries.map((entry, idx) => (
        <Snackbar
          key={entry.id}
          open
          autoHideDuration={5000}
          onClose={() => handleClose(entry.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            position: 'static',
            transform: `translateY(${idx * 4}px)`,
          }}
        >
          <Alert
            severity={SEVERITY_BY_EVENT[entry.payload.event]}
            onClick={() => handleClick(entry.payload, entry.id)}
            onClose={() => handleClose(entry.id)}
            sx={{ cursor: 'pointer', minWidth: 320 }}
            variant="filled"
          >
            <Typography variant="body2" component="div">
              {buildMessage(entry.payload)}
            </Typography>
            {entry.payload.byUser && (
              <Typography variant="caption" color="rgba(255,255,255,0.85)">
                user: {entry.payload.byUser}
              </Typography>
            )}
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
}
